import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, CopyIcon, Tooltip, cn } from '@/design-system';

export interface ReferenceIdProps {
  value: string;
  /** App path to open, e.g. "/deals/deal-123". */
  to?: string;
  copyable?: boolean;
  className?: string;
}

/** Deal, invoice, payment and bank references: mono, left-to-right, copyable. */
export function ReferenceId({ value, to, copyable = true, className }: ReferenceIdProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const text = (
    <bdi dir="ltr" className="font-mono text-[0.93em] tracking-tight">
      {value}
    </bdi>
  );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable: the value is still selectable */
    }
  };
  return (
    <span className={cn('group/ref inline-flex items-center gap-1 whitespace-nowrap', className)}>
      {to ? (
        <Link to={to as never} className="text-accent hover:text-accent-hover hover:underline">
          {text}
        </Link>
      ) : (
        text
      )}
      {copyable ? (
        <Tooltip content={copied ? t('actions.copied') : t('actions.copy')}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void copy();
            }}
            aria-label={`${t('actions.copy')} ${value}`}
            className="inline-flex size-5 items-center justify-center rounded-sm text-ink-muted opacity-0 group-hover/ref:opacity-100 hover:bg-surface-muted hover:text-ink focus-visible:opacity-100"
          >
            {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
          </button>
        </Tooltip>
      ) : null}
    </span>
  );
}
